import type { LucideIcon, LucideProps } from 'lucide-react'

import { cn } from '@/libs/utils'

export type ThemeIconVariant =
  | 'default'
  | 'primary'
  | 'muted'
  | 'accent'
  | 'destructive'
  | 'success'
  | 'warning'
  | 'info'
  | 'sidebar'

/**
 * Standardized icon sizes.
 *
 * These map to the `--icon-size-*` CSS variables in `index.css`, keeping
 * every icon in the app on the same scale at every breakpoint.
 *
 *  - `xs` 12px — dense inline glyphs (badges, tag chips)
 *  - `sm` 14px — table cells, small buttons
 *  - `md` 16px — default UI icon (nav items, form fields, inline)
 *  - `lg` 20px — primary buttons, section headers
 *  - `xl` 24px — feature callouts, empty states
 *  - `xxl` 32px — hero / illustration slots
 *  - `responsive` — 16 → 20 → 24 across sm / lg breakpoints
 */
export type ThemeIconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'responsive'

export interface ThemeIconProps extends Omit<LucideProps, 'color'> {
  icon: LucideIcon
  variant?: ThemeIconVariant
  /** Preferred over `size` (px) — keeps every icon on the same scale. */
  size?: ThemeIconSize | number
}

const variantMap: Record<ThemeIconVariant, string> = {
  default: 'text-foreground',
  primary: 'text-primary',
  muted: 'text-muted-foreground',
  accent: 'text-accent',
  destructive: 'text-destructive',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-primary',
  sidebar: 'text-sidebar-foreground',
}

const sizeMap: Record<ThemeIconSize, string> = {
  xs: 'icon-xs',
  sm: 'icon-sm',
  md: 'icon-md',
  lg: 'icon-lg',
  xl: 'icon-xl',
  xxl: 'icon-xxl',
  responsive: 'icon-responsive',
}

/**
 * Theme-aware Lucide icon wrapper.
 *
 * Colors resolve through semantic CSS variables, so they adapt automatically
 * when `.dark` is applied. Stroke width is controlled by `--icon-stroke-width`
 * (slightly heavier in dark mode for better visibility). Sizes come from the
 * shared `--icon-size-*` scale so every icon stays consistent at every
 * breakpoint.
 */
export function ThemeIcon({
  icon: Icon,
  variant = 'default',
  size = 'md',
  className,
  ...props
}: ThemeIconProps) {
  const sizeClass = typeof size === 'string' ? sizeMap[size] : undefined
  const pixelSize = typeof size === 'number' ? size : undefined
  return (
    <Icon
      className={cn(
        'shrink-0',
        variantMap[variant],
        sizeClass,
        'stroke-[var(--icon-stroke-width,2)]',
        className,
      )}
      {...(pixelSize !== undefined ? { size: pixelSize } : {})}
      {...props}
    />
  )
}
